<script setup lang="ts">
import checkmarkIcon from '~/assets/svg/icon-checkmark.svg?url';
import greenCheckmarkIcon from '~/assets/svg/icon-checkmark-green.svg?url';
import tiptappLogo from '~/assets/svg/tiptapp-transparent-logo.svg?url';
export interface AdData {
    ccy: string;
    pay: number;
    imageUrls: string[];
    desc: string;
    title: string;
    area: string;
    country: string;
    state: string;
    created: number;
    type: string;
    pickedUpTime?: number;
}

const props = defineProps<{
    modelValue?: AdData;
    additionalComponents?: any[];
    ctaText?: string;
    ctaLink?: { cached_url: string; target: string };
}>();

const ad = computed(() => props.modelValue || ({} as AdData));
const { t } = useI18n();

const formatTimeAgo = (timestamp: number) => {
    if (!timestamp) return '';
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
        return t('ad.publishedMinutesAgo', { minutes });
    } else if (hours < 24) {
        return t('ad.publishedHoursAgo', { hours });
    } else {
        return t('ad.publishedDaysAgo', { days });
    }
};

function formatTimeBetween(start: number, end: number) {
    const diff = end - start;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) {
        return t('days', { days: days + 1 });
    } else if (minutes > 58) {
        return t('hours', { hours: hours + 1 });
    } else {
        return t('minutes', { minutes: minutes + 1 });
    }
}
function renderPay(pay: number, ccy: string | undefined) {
    if (ccy === 'GBP') return '£' + pay;
    if (ccy === 'EUR') return '€' + pay;
    if (ccy === 'SEK') return pay + ' kr';
    return pay + ' – –';
}
</script>

<template>
    <div class="bg-white dark:bg-midnight-blue">
        <!-- Ad content -->
        <main class="max-w-4xl mx-auto px-4 py-6">
            <!-- Split layout: Image and Details -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <!-- Ad image with overlays -->
                <div v-if="ad.imageUrls && ad.imageUrls.length > 0" class="relative">
                    <div
                        class="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden"
                    >
                        <img
                            :src="ad.imageUrls[0]"
                            :alt="ad.title"
                            class="w-full h-full object-cover"
                        />
                    </div>
                </div>

                <!-- Ad details -->
                <div class="space-y-4">
                    <h1
                        v-if="ad.title"
                        class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white"
                    >
                        {{ ad.title }}
                    </h1>

                    <div
                        class="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-300"
                    >
                        <div v-if="ad.created" class="flex items-center gap-1">
                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                            {{ formatTimeAgo(ad.created) }}
                        </div>

                        <div v-if="ad.area" class="flex items-center gap-1">
                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                            {{ ad.area }}
                        </div>
                    </div>

                    <div v-if="ad.pay">
                        <p class="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {{ t('ad.pay') }}:
                            {{ renderPay(ad.pay, ad.ccy) }}
                        </p>
                    </div>

                    <div v-if="ad.desc">
                        <p class="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {{ ad.desc }}
                        </p>
                    </div>

                    <div v-if="ad.state && (ad.state === 'assigned' || ad.state === 'pickedUp')">
                        <div class="flex items-center gap-2 mb-2">
                            <img
                                v-if="ad.state === 'pickedUp'"
                                :src="checkmarkIcon"
                                alt="Checkmark"
                                class="w-6 h-6"
                            />
                            <img
                                v-if="ad.state === 'assigned'"
                                :src="greenCheckmarkIcon"
                                alt="Green checkmark"
                                class="w-6 h-6"
                            />
                            <h4
                                class="text-gray-700 dark:text-gray-300 leading-relaxed font-semibold"
                            >
                                {{ t(`ad.state.${ad.state}.title`) }}
                                {{
                                    ad.pickedUpTime && ad.state === 'pickedUp'
                                        ? formatTimeBetween(ad.created, ad.pickedUpTime)
                                        : ''
                                }}
                            </h4>
                        </div>
                        <p class="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {{ t(`ad.state.${ad.state}.description`) }}
                        </p>
                    </div>
                    <div v-if="ad.type && ctaLink && ctaText" class="mt-4">
                        <a
                            :href="ctaLink.cached_url"
                            :target="ctaLink.target"
                            class="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        >
                            {{ ctaText }}
                        </a>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>
